import { useEffect, useMemo } from 'react';
import { useFormik, FormikProvider, type FormikProps } from 'formik';
import { Box, Flex } from '@chakra-ui/react';
import { Checkbox } from '@kula-ai/components';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { KText } from '@/ui-library/components/text';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { FormItem } from '@/components/formBuilder/components/FormItem';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { ScheduleRejectionEmail } from '@/components/shared/rejection-email/ScheduleRejectionEmail';
import {
  AUTO_REJECT_EMAIL_DEFAULTS,
  type AutoRejectEmailFormValues,
} from '../autoRejectEmail.types';

type FormValues = AutoRejectEmailFormValues;

// Minimal shape of a rejection email template from
// /ats_comm_templates?filter_by[category][]=candidate_application_rejected.
interface EmailTemplate {
  id: number;
  name: string;
  subject?: string;
  body?: string;
  from?: string;
  cc?: string[];
  bcc?: string[];
  attachments?: unknown[];
}

const REJECTION_EMAIL_VARIABLES_VIEW = ['sender', 'job', 'candidate', 'org'];

// NOTE: this mirrors the fraud-detector `InlineEmailSection`
// (FraudDetectorDrawerForm.tsx). Candidate for extraction into a shared
// rejection-email component once both features settle (ENG-3114).
const InlineEmailSection = ({
  formik,
  templateOptions,
}: {
  formik: FormikProps<FormValues>;
  templateOptions: EmailTemplate[];
}) => {
  const emailFormRef = useMemo(() => ({ current: formik }), [formik]);

  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view: REJECTION_EMAIL_VARIABLES_VIEW },
  });

  const emailFields = useEmailField({
    formRef: emailFormRef,
    userEmailType: 'hiringTeam',
    templateData: formik?.values || {},
    variables,
  });

  const { values, setValues } = formik;
  const selectedTemplateId = (values.selectedTemplate as { id?: number } | null)
    ?.id;

  const selectedTemplateOption = useMemo(() => {
    if (!selectedTemplateId) return null;
    const template = templateOptions.find((t) => t.id === selectedTemplateId);
    if (!template) return null;
    return { value: template, label: template.name, body: template.body };
  }, [selectedTemplateId, templateOptions]);

  const handleTemplateChange = (
    opt: { value: EmailTemplate; label: string; body?: string } | null
  ) => {
    if (!opt) {
      setValues({ ...values, selectedTemplate: null, subject: '', body: '' });
      return;
    }
    const template = opt.value;
    setValues({
      ...values,
      selectedTemplate: template,
      subject: template.subject || '',
      body: template.body || '',
      from: template.from || values.from,
      cc: template.cc || [],
      bcc: template.bcc || [],
      attachments: template.attachments || [],
    });
  };

  return (
    <Box>
      <Box mb="12px">
        <KText
          fontSize="13px"
          fontWeight="550"
          color="secondary-900"
          display="block"
          mb="6px"
        >
          Rejection email template{' '}
          <KText as="span" color="error-200">
            *
          </KText>
        </KText>
        <KSelect
          options={templateOptions.map((t) => ({
            value: t,
            label: t.name,
            body: t.body,
          }))}
          value={selectedTemplateOption}
          onChange={handleTemplateChange}
          placeholder="Select email template"
          isClearable
          menuPortalTarget={document.body}
          menuPlacement="auto"
        />
      </Box>

      {!!selectedTemplateId && (
        <Box
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          mb="12px"
        >
          {emailFields.map((field: any, i: number) => (
            <FormItem key={i} field={field} formik={formik} />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface AutoRejectEmailSectionProps {
  /**
   * The parent reads `formRef.current.values` at save time to build the
   * payload. Mirrors the fraud-detector `formRef` pattern so the drawer's
   * local-state save flow can stay non-Formik.
   */
  formRef: React.MutableRefObject<FormikProps<FormValues> | null>;
  initialValues?: FormValues;
  /** Notifies the parent when the email form's dirty state changes, so the
   * drawer can enable/disable its save / Test Scoring agent buttons. */
  onDirtyChange?: (_dirty: boolean) => void;
}

/**
 * Self-contained Formik "island" for configuring the AI-scoring auto-reject
 * rejection email: a "Send rejection email" checkbox that reveals the template
 * selector + email editor (via the shared `useEmailField`) and the
 * scheduling controls. Used inside the auto-reject section of the Scoring agent
 * drawer/edit view.
 */
export const AutoRejectEmailSection = ({
  formRef,
  initialValues,
  onDirtyChange,
}: AutoRejectEmailSectionProps) => {
  const formik = useFormik<FormValues>({
    initialValues: initialValues ?? AUTO_REJECT_EMAIL_DEFAULTS,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  // Expose the live formik instance to the parent for save-time reads.
  useEffect(() => {
    formRef.current = formik;
  }, [formRef, formik]);

  // Report dirty changes so the drawer can toggle its save / test buttons.
  const { dirty } = formik;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const { data: templateOptions = [] } = useQuery<EmailTemplate[]>({
    queryKey: ['aiScoringAutoReject', 'emailTemplates'],
    queryFn: async () => {
      const res = (await http.get(
        '/ats_comm_templates?filter_by[category][]=candidate_application_rejected&items=200&filter_by[name]='
      )) as { data: EmailTemplate[] };
      return res?.data ?? [];
    },
    staleTime: 0,
  });

  const { values, setFieldValue, setValues } = formik;
  const { sendRejectEmail } = values;

  return (
    // FormikProvider shares this island's useFormik() instance via context so
    // the reused email fields (which use Formik's <Field>/useField internally)
    // resolve getFieldProps — FormBuilder does the same via its <Formik>.
    <FormikProvider value={formik}>
      <Box mt="16px">
        <Flex gap="8px" alignItems="center">
          <Checkbox
            size="sm"
            label="Send rejection email"
            labelClassName="!text-sm !font-[550] !text-secondary-900"
            checked={sendRejectEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              if (!checked) {
                setValues({
                  ...values,
                  sendRejectEmail: false,
                  selectedTemplate: null,
                  subject: '',
                  body: '',
                });
              } else {
                setFieldValue('sendRejectEmail', true);
              }
            }}
          />
        </Flex>

        {sendRejectEmail && (
          <Box mt="12px">
            <InlineEmailSection
              formik={formik}
              templateOptions={templateOptions}
            />
            <Box mt="12px">
              <ScheduleRejectionEmail formik={formik as any} />
            </Box>
          </Box>
        )}
      </Box>
    </FormikProvider>
  );
};
